/**
 * The one description of "this company's teams, as this viewer may read them".
 *
 * Pure on purpose: the jest harness cannot mount a component or reach Firestore,
 * so the query's shape is locked here and `CompanyTeamsBindingMixin` only applies
 * it. The company-role Add dialog's team picker binds through that mixin, so
 * this is the only place the query is spelled out. (The company dashboard's
 * table pages its teams from Postgres instead.)
 *
 * Lives beside the mixin (not in a view folder) so the dependency points the
 * usual way: views import mixins, never the reverse. It is a helper, not a
 * mixin, so the mixins barrel does not export it.
 */

export type CompanyTeamsQueryWhere = [string, '==', string | boolean];

export interface CompanyTeamsQueryDescription {
	collection: 'teams'
	where: CompanyTeamsQueryWhere[]
	limit: number
}

export interface CompanyTeamsQueryInput {
	companyId: string | null
	// The viewer holds MANAGE_TEAMS (TeamUserMixin.hasManageTeamsPermission)
	canManageTeams: boolean
	viewerUserId: string | null
}

export const COMPANY_TEAMS_QUERY_LIMIT: number = 4000;

/**
 * Returns null when there is no company to query, which the mixin treats as
 * "unbind and show nothing".
 *
 * A viewer without MANAGE_TEAMS gets the `userIds.<uid>` narrowing. That is not
 * a nicety: the `teams/{id}` read rule only admits a non-manager to teams they
 * are on, and Firestore refuses a whole query that could return a doc the rules
 * would deny. Dropping the narrowing turns the list into a permission error.
 */
export const describeCompanyTeamsQuery = (input: CompanyTeamsQueryInput): CompanyTeamsQueryDescription | null => {
	if (!input.companyId) return null;

	const where: CompanyTeamsQueryWhere[] = [
		['companyId', '==', input.companyId],
		['isActive', '==', true],
	];
	if (!input.canManageTeams) {
		where.push([`userIds.${input.viewerUserId}`, '==', true]);
	}

	return {
		collection: 'teams',
		where,
		limit: COMPANY_TEAMS_QUERY_LIMIT,
	};
};
